import { useCallback, useEffect, useState } from 'react';
import config from '../config';
import {
  Error as ApiError,
  Flow,
  FlowRun,
  FlowRunResult,
  FlowTestRequest,
  PaginatedResponse,
} from '../types';
import prepareHeaders from '../utilities/prepare-headers';
import { useAuth } from './auth';

/**
 * An error from the API, with its code and details (a flow's diagnostics,
 * failing tests and so on)
 */
export class FlowApiError extends Error {
  public readonly code: number | undefined;
  public readonly details: Record<string, any> | undefined;

  public constructor(error: ApiError & { details?: Record<string, any> }) {
    super(error.message);
    this.code = (error as any).code;
    this.details = error.details;
  }
}

const useFlows = () => {
  const { token } = useAuth();

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<FlowApiError | null>(null);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const request = useCallback(
    async <T>(
      path: string,
      method: string,
      body?: unknown,
      signal?: AbortSignal
    ): Promise<T | void> => {
      setPending(true);
      setError(null);

      return fetch(`${config.api.url}${path}`, {
        method,
        headers: prepareHeaders(token),
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      })
        .then(async response => {
          setPending(false);

          if (!response.ok) {
            setError(new FlowApiError(await response.json()));
            return;
          }

          if (response.status === 204) {
            return true as T;
          }

          return (await response.json()) as T;
        })
        .catch((error: Error) => {
          setPending(false);
          if (error.name !== 'AbortError') {
            setError(new FlowApiError({ message: error.message } as ApiError));
          }
        });
    },
    [token]
  );

  const index = useCallback(
    async (
      options: { page?: number; limit?: number } = {},
      signal?: AbortSignal
    ): Promise<PaginatedResponse<Flow> | void> =>
      request<PaginatedResponse<Flow>>(
        `/flows?${new URLSearchParams({
          page: (options.page ?? 1).toString(),
          limit: (options.limit ?? 20).toString(),
        })}`,
        'GET',
        undefined,
        signal
      ),
    [request]
  );

  const show = useCallback(
    async (flowId: string, signal?: AbortSignal): Promise<Flow | void> =>
      request<Flow>(`/flows/${flowId}`, 'GET', undefined, signal),
    [request]
  );

  const create = useCallback(
    async (data: {
      document: unknown;
      tests?: unknown;
      activated?: boolean;
    }): Promise<Flow | void> => request<Flow>('/flows', 'POST', data),
    [request]
  );

  const update = useCallback(
    async (
      flowId: string,
      data: { document?: unknown; tests?: unknown; activated?: boolean }
    ): Promise<Flow | void> => request<Flow>(`/flows/${flowId}`, 'PUT', data),
    [request]
  );

  const delete_ = useCallback(
    async (flowId: string): Promise<boolean | void> =>
      request<boolean>(`/flows/${flowId}`, 'DELETE'),
    [request]
  );

  const test = useCallback(
    async (
      flowId: string,
      data: FlowTestRequest
    ): Promise<FlowRunResult | void> =>
      request<FlowRunResult>(`/flows/${flowId}/test`, 'POST', data),
    [request]
  );

  const run = useCallback(
    async (
      flowId: string,
      data: { input?: any; identityId?: string }
    ): Promise<FlowRunResult | void> =>
      request<FlowRunResult>(`/flows/${flowId}/run`, 'POST', data),
    [request]
  );

  const indexRuns = useCallback(
    async (
      flowId: string,
      options: { page?: number; limit?: number; status?: string } = {},
      signal?: AbortSignal
    ): Promise<PaginatedResponse<FlowRun> | void> =>
      request<PaginatedResponse<FlowRun>>(
        `/flows/${flowId}/runs?${new URLSearchParams({
          page: (options.page ?? 1).toString(),
          limit: (options.limit ?? 20).toString(),
          ...(options.status ? { status: options.status } : {}),
        })}`,
        'GET',
        undefined,
        signal
      ),
    [request]
  );

  const showRun = useCallback(
    async (
      flowId: string,
      runId: string,
      signal?: AbortSignal
    ): Promise<FlowRun | void> =>
      request<FlowRun>(
        `/flows/${flowId}/runs/${runId}`,
        'GET',
        undefined,
        signal
      ),
    [request]
  );

  return {
    pending,
    error,
    resetError,
    index,
    show,
    create,
    update,
    delete: delete_,
    test,
    run,
    indexRuns,
    showRun,
  };
};

/**
 * The user's flows' names by id (the first 100), for showing and picking
 * flows in token permissions
 */
export const useFlowNames = (enabled = true): Record<string, string> => {
  const { index } = useFlows();
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const abortController = new AbortController();
    index({ limit: 100 }, abortController.signal).then(response => {
      if (response) {
        setNames(
          Object.fromEntries(response.data.map(flow => [flow.id, flow.name]))
        );
      }
    });
    return () => abortController.abort();
  }, [enabled, index]);

  return names;
};

export default useFlows;
